import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaUserPlus, FaTrashAlt, FaSyncAlt, FaUserShield, FaExclamationTriangle } from 'react-icons/fa';
import {
  TechInput, TechSelect, TechButton, TechButtonGhost, StatusPill, Empty, Loading, ErrorBox, borderSoft, mono, accent,
} from '../../styles/techComponents';
import { useAuth } from '../../contexts/AuthContext';
import { listUsers, createUser, updateUser, deleteUser } from '../../services/usersService';

const ROLE_LABEL = {
  admin: 'Administrador', estratega: 'Estratega', comunicador: 'Comunicador',
  analista: 'Analista', referente: 'Referente territorial', visualizador: 'Visualizador',
};
const ROLE_HINT = {
  admin: 'Acceso completo + gestión de usuarios y acciones destructivas.',
  estratega: 'Casi todo salvo administración de usuarios.',
  comunicador: 'Mensajería, materiales y bandeja de contenido.',
  analista: 'Lectura + investigación y análisis.',
  referente: 'Gestión territorial (reclamos y recorridas).',
  visualizador: 'Solo lectura de tableros y reportes.',
};

const Row = styled.div`
  display: grid; grid-template-columns: 1.6fr 1.2fr auto auto; gap: 0.6rem; align-items: center;
  padding: 0.6rem 0; border-bottom: 1px solid ${borderSoft};
  @media (max-width: 720px){ grid-template-columns: 1fr; }
`;
const UMeta = styled.div` .n{font-weight:600} .e{font-family:${mono};font-size:0.75rem;color:var(--text-secondary)} `;
const AddCard = styled.div`
  border: 1px solid ${borderSoft}; border-radius: 8px; padding: 0.9rem; margin-top: 1rem;
  display: grid; grid-template-columns: 1fr 1fr 1fr 1fr auto; gap: 0.5rem; align-items: end;
  @media (max-width: 900px){ grid-template-columns: 1fr 1fr; }
`;
const Lbl = styled.label` font-family:${mono}; font-size:0.66rem; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-secondary); display:block; margin-bottom:0.25rem; `;

export default function UsersPanel() {
  const { user } = useAuth();
  const isAdmin = (user?.role || 'admin') === 'admin';
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(Object.keys(ROLE_LABEL));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'analista', password: '' });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const d = await listUsers(); setUsers(d.users || []); if (d.roles) setRoles(d.roles); }
    catch (e) { setError(e?.response?.data?.error || 'No se pudo cargar (¿sos admin?)'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  async function onChangeRole(u, role) {
    try { const upd = await updateUser(u.id, { role }); setUsers((p) => p.map((x) => x.id === u.id ? upd : x)); flash('Rol actualizado'); }
    catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }
  async function onToggleActive(u) {
    try { const upd = await updateUser(u.id, { active: !u.active }); setUsers((p) => p.map((x) => x.id === u.id ? upd : x)); }
    catch (e) { flash(e?.response?.data?.error || 'Error'); }
  }
  async function onDelete(u) {
    try { await deleteUser(u.id); setUsers((p) => p.filter((x) => x.id !== u.id)); flash('Usuario eliminado'); }
    catch (e) { flash(e?.response?.data?.error || 'No se pudo eliminar'); }
  }
  async function onAdd() {
    if (!form.email || form.password.length < 8) return flash('Email y contraseña (mín. 8) requeridos');
    try {
      const u = await createUser(form);
      setUsers((p) => [...p, u]);
      setForm({ email: '', name: '', role: 'analista', password: '' });
      flash('Usuario creado — pasale email y contraseña');
    } catch (e) { flash(e?.response?.data?.error || 'No se pudo crear'); }
  }

  if (!isAdmin) return <Empty><FaUserShield /> La gestión de usuarios es solo para administradores.</Empty>;
  if (loading) return <Loading>Cargando usuarios…</Loading>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <StatusPill>{users.length} usuarios</StatusPill>
        <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>
      </div>
      {error && <ErrorBox><FaExclamationTriangle /> {error}</ErrorBox>}
      {msg && <StatusPill style={{ marginBottom: '0.5rem' }}>{msg}</StatusPill>}

      {users.map((u) => (
        <Row key={u.id}>
          <UMeta><div className="n">{u.name || '(sin nombre)'}</div><div className="e">{u.email}</div></UMeta>
          <div>
            <TechSelect value={u.role} onChange={(e) => onChangeRole(u, e.target.value)} title={ROLE_HINT[u.role]}>
              {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
            </TechSelect>
          </div>
          <TechButtonGhost type="button" onClick={() => onToggleActive(u)} style={{ color: u.active ? 'var(--alert-success)' : 'var(--text-secondary)' }}>
            {u.active ? 'Activo' : 'Inactivo'}
          </TechButtonGhost>
          <TechButtonGhost type="button" onClick={() => onDelete(u)} title="Eliminar"><FaTrashAlt /></TechButtonGhost>
        </Row>
      ))}
      {users.length === 0 && <Empty>Sin usuarios cargados.</Empty>}

      <AddCard>
        <div><Lbl>Email</Lbl><TechInput type="email" placeholder="persona@equipo.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Lbl>Nombre</Lbl><TechInput placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Lbl>Rol</Lbl><TechSelect value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>{roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}</TechSelect></div>
        <div><Lbl>Contraseña temporal</Lbl><TechInput type="text" placeholder="mín. 8 caracteres" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        <TechButton type="button" onClick={onAdd}><FaUserPlus /> Crear</TechButton>
      </AddCard>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.6rem', fontFamily: mono }}>
        Onboarding: creá el usuario acá, pasale email + contraseña, y que le mande <span style={{ color: accent }}>/start</span> al bot para recibir el parte diario.
      </p>
    </div>
  );
}
